import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Loader2, Gauge } from "lucide-react";
import {
  DISCIPLINES, SPORT_REF_DEFS, parsePaceKmToSec, parsePace100mToSec,
  formatPaceKm, formatPace100m,
} from "@/lib/trainingDisciplines";

function refToDisplay(key, value) {
  if (value == null || value === "") return "";
  if (key === "threshold_pace_sec_km") return formatPaceKm(value).replace("/km", "");
  if (key === "css_sec_100m") return formatPace100m(value).replace("/100m", "");
  return String(value);
}

function displayToRef(key, display) {
  if (!display) return null;
  if (key === "threshold_pace_sec_km") return parsePaceKmToSec(display);
  if (key === "css_sec_100m") return parsePace100mToSec(display);
  const n = parseFloat(display);
  return Number.isNaN(n) ? null : n;
}

export default function SportRefsPanel({ studentId, discipline, values, onChange, onSaved, compact = false }) {
  const [saving, setSaving] = useState(false);
  const d = DISCIPLINES[discipline];
  const keys = d?.sportRefKeys || [];

  if (!studentId || keys.length === 0) return null;

  const save = async () => {
    setSaving(true);
    try {
      const refs = keys.map((key) => ({
        ref_key: key,
        value: displayToRef(key, values[key]),
      })).filter((r) => r.value != null);
      const { data } = await api.put("/sport-refs", { student_id: studentId, refs });
      onSaved?.(data || []);
      toast.success("Références enregistrées");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white ${compact ? "p-3" : "p-4"} space-y-3`}>
      <div className="flex items-center gap-2">
        <Gauge className="w-4 h-4 text-slate-600" />
        <p className="text-sm font-semibold text-slate-800">Références endurance</p>
      </div>
      <div className={`grid gap-3 ${keys.length > 2 ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
        {keys.map((key) => {
          const def = SPORT_REF_DEFS[key];
          return (
            <div key={key}>
              <Label className="text-xs text-slate-500">{def.label}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={values[key] ?? ""}
                  onChange={(e) => onChange({ ...values, [key]: e.target.value })}
                  placeholder={def.placeholder}
                  className="h-9"
                />
                <span className="text-xs text-slate-400 shrink-0 w-14">{def.unit}</span>
              </div>
            </div>
          );
        })}
      </div>
      <Button type="button" size="sm" variant="secondary" onClick={save} disabled={saving} className="gap-1.5">
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        Enregistrer
      </Button>
    </div>
  );
}

export function sportRefsFromApi(refs = []) {
  const out = {};
  refs.forEach((r) => {
    out[r.ref_key] = refToDisplay(r.ref_key, r.value);
  });
  return out;
}